import { AxiosError } from 'axios'
import { FileSignature, SearchIcon } from 'lucide-react'
import ListHeader from '@/common/components/ListHeader'
import ListFilters from '@/common/components/ListFilters'
import TextField from '@/common/components/TextField'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { Mapper } from '@/common/services/mapper'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import useDebounce from '@/common/hooks/useDebounce'
import { useCompanies } from '@/common/hooks/queries/useCompanies'
import { CompaniesTransformer } from '@/common/services/companiesTransformer'
import { Option } from '@/common/types/option'
import { useMaintenanceManualEntryProposals } from '../../hooks/useMaintenanceManualEntryProposals'
import { MaintenanceManualEntryProposalsTransformer } from '../../services/maintenanceManualEntryProposalsTransformer'
import { ManualEntryProposalCardItem } from '../../types/maintenanceManualEntryProposal.type'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type'
import ProposalKanbanColumn from '../../components/ProposalKanbanColumn'
import ProposalCard from '../../components/ProposalCard'

const isForbidden = (error: unknown) => (error as AxiosError | null)?.response?.status === 403

const directiveTypeOptions: Option[] = [
  { label: 'Diagnóstico', value: MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry },
  { label: 'Execução', value: MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry },
]

const MaintenanceProposalsList = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const [search, setSearch] = useDebounce((filterParams.search as string) || '')
  const [searchCompany, setSearchCompany] = useDebounce('')

  const { data: clientCompanyOptions } = useCompanies({
    filters: { search: searchCompany },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  })

  const { date: createdAtDate, formattedDate: createdAtFormatted } = useFilterDate({ date: filterParams.createdAt })
  const { date: completedAtDate, formattedDate: completedAtFormatted } = useFilterDate({ date: filterParams.completedAt })
  const { date: canceledAtDate, formattedDate: canceledAtFormatted } = useFilterDate({ date: filterParams.canceledAt })

  const sharedFilters = {
    search,
    directiveEntryType: (filterParams.directiveType as Option | undefined)?.value,
    clientCompanyId: (filterParams.clientCompany as Option | undefined)?.value,
    createdAtGteq: createdAtFormatted.from,
    createdAtLteq: createdAtFormatted.to,
    completedAtGteq: completedAtFormatted.from,
    completedAtLteq: completedAtFormatted.to,
    canceledAtGteq: canceledAtFormatted.from,
    canceledAtLteq: canceledAtFormatted.to,
  }

  const awaitingYou = useMaintenanceManualEntryProposals<ManualEntryProposalCardItem[]>({
    filters: { ...sharedFilters, status: 'awaiting_establishment_response' },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceManualEntryProposalsTransformer.cardsTransformer('awaiting_establishment_response'),
    }),
  })

  const awaitingClient = useMaintenanceManualEntryProposals<ManualEntryProposalCardItem[]>({
    filters: { ...sharedFilters, status: 'awaiting_client_response' },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceManualEntryProposalsTransformer.cardsTransformer('awaiting_client_response'),
    }),
  })

  const accepted = useMaintenanceManualEntryProposals<ManualEntryProposalCardItem[]>({
    filters: { ...sharedFilters, status: 'completed' },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceManualEntryProposalsTransformer.cardsTransformer('completed'),
    }),
  })

  const canceled = useMaintenanceManualEntryProposals<ManualEntryProposalCardItem[]>({
    filters: { ...sharedFilters, status: 'canceled' },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceManualEntryProposalsTransformer.cardsTransformer('canceled'),
    }),
  })

  const finalized = [...accepted.data, ...canceled.data]

  return (
    <div className="inner-screen gap-6 overflow-hidden">
      <ListHeader
        Icon={<FileSignature className="w-10 h-10" strokeWidth={1.5} />}
        title="Propostas de manutenção"
      />

      <div className="flex flex-col gap-4">
        <TextField
          LeftIcon={<SearchIcon />}
          placeholder="Pesquisar por protocolo, placa ou empresa"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          value={(filterParams.search as string) || ''}
          onChange={(e) => {
            const value = (e.target as HTMLInputElement).value
            setSearch(value)
            setFilterParams((prev) => ({ ...prev, search: value }))
          }}
        />

        <div className="flex items-center gap-2 flex-wrap">
          <DatePickerWithRange
            date={createdAtDate}
            setDate={(date) => setFilterParams((prev) => ({ ...prev, createdAt: date }))}
            placeholder="Criação"
          />

          <DatePickerWithRange
            date={completedAtDate}
            setDate={(date) => setFilterParams((prev) => ({ ...prev, completedAt: date }))}
            placeholder="Conclusão"
          />

          <DatePickerWithRange
            date={canceledAtDate}
            setDate={(date) => setFilterParams((prev) => ({ ...prev, canceledAt: date }))}
            placeholder="Cancelamento"
          />

          <ListFilters
            filters={[
              {
                className: 'min-w-52',
                dataTestid: 'clientCompany',
                placeholder: 'Empresa cliente',
                value: (filterParams.clientCompany as Option | null) || null,
                options: [{ label: 'Todas', value: '' }, ...((clientCompanyOptions as Option[]) || [])],
                onInputChange: setSearchCompany,
                onChange: (e) => setFilterParams((prev) => ({ ...prev, clientCompany: e })),
              },
              {
                className: 'min-w-40',
                dataTestid: 'directiveType',
                placeholder: 'Tipo de diretiva',
                value: (filterParams.directiveType as Option | null) || null,
                options: [{ label: 'Todos', value: '' }, ...directiveTypeOptions],
                onChange: (e) => setFilterParams((prev) => ({ ...prev, directiveType: e })),
              },
            ]}
          />
        </div>
      </div>

      <div className="flex gap-4 flex-1 overflow-x-auto overflow-y-hidden">
        <ProposalKanbanColumn
          title="Aguardando sua resposta"
          count={awaitingYou.data.length}
          accentClassName="bg-yellow-400"
          isLoading={awaitingYou.isLoading}
          isError={awaitingYou.isError}
          forbidden={isForbidden(awaitingYou.error)}
        >
          {awaitingYou.data.map((proposal) => (
            <ProposalCard key={proposal.id} data={proposal} accentClassName="bg-yellow-400" />
          ))}
        </ProposalKanbanColumn>

        <ProposalKanbanColumn
          title="Aguardando resposta do cliente"
          count={awaitingClient.data.length}
          accentClassName="bg-primary-blue"
          isLoading={awaitingClient.isLoading}
          isError={awaitingClient.isError}
          forbidden={isForbidden(awaitingClient.error)}
        >
          {awaitingClient.data.map((proposal) => (
            <ProposalCard key={proposal.id} data={proposal} accentClassName="bg-primary-blue" />
          ))}
        </ProposalKanbanColumn>

        <ProposalKanbanColumn
          title="Finalizadas"
          count={finalized.length}
          isLoading={accepted.isLoading || canceled.isLoading}
          isError={accepted.isError || canceled.isError}
          forbidden={isForbidden(accepted.error) || isForbidden(canceled.error)}
        >
          {finalized.map((proposal) => (
            <ProposalCard key={proposal.id} data={proposal} />
          ))}
        </ProposalKanbanColumn>
      </div>
    </div>
  )
}

export default MaintenanceProposalsList
